// ============================================================================
// CALENDAR VIEW — a month of free inventory
// ============================================================================
// The same OccupancyDayCell as the rolling strip, laid out as a month. One data
// model at two densities: the strip answers "the coming days", the calendar
// answers "this month", and clicking a day in either lands on the same Day View.
//
// Deliberately NUMERIC cells — no booking chips inside them. Guest names in
// month cells produce forty unreadable chips on a busy Saturday, which is
// rebuilding the grid badly inside a calendar. The cell answers "how much is
// unsold"; the Day View answers "who".
//
// Design: crs-doc/CRS-Booking-Worksheet-Day-View-Design.md §10 Phase 2.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { availabilityService } from 'api/services/availabilityService';
import type { OccupancyHorizonDay } from 'api/types/availability.types';
import OccupancyDayCell from './OccupancyDayCell';
import { parseDayKey, toDayKey, todayKey } from 'utils/calendarDate';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface OccupancyCalendarProps {
  propertyId: string;
  /** Highlighted day (yyyy-MM-dd). */
  selectedDate: string;
  /** A day was activated — the worksheet sends it to the Day View. */
  onSelectDate: (isoDate: string) => void;
  /** Bumped by the worksheet after any mutation. */
  refreshKey: number;
}

const OccupancyCalendar: React.FC<OccupancyCalendarProps> = ({
  propertyId, selectedDate, onSelectDate, refreshKey,
}) => {
  // Anchor on the 1st of the shown month.
  const [anchor, setAnchor] = useState<string>(() => {
    const d = new Date();
    return toDayKey(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [days, setDays] = useState<OccupancyHorizonDay[]>([]);
  const [highVacancyCount, setHighVacancyCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const monthMeta = useMemo(() => {
    const first = parseDayKey(anchor);
    const year = first.getFullYear();
    const month = first.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // Monday-first grid: JS getDay() is Sunday-based, so shift it.
    const leading = (first.getDay() + 6) % 7;
    return { first, year, month, daysInMonth, leading };
  }, [anchor]);

  const load = useCallback(async () => {
    if (!propertyId) { setDays([]); return; }
    setLoading(true);
    setError('');
    try {
      // The horizon endpoint is date-range based, so a month is just a window.
      const res = await availabilityService.getOccupancyHorizon(
        propertyId, anchor, monthMeta.daysInMonth,
      );
      setDays(res.data?.days ?? []);
      setHighVacancyCount(res.data?.highVacancyDayCount ?? 0);
    } catch {
      setDays([]);
      setError('Could not load the inventory position for this month.');
    } finally {
      setLoading(false);
    }
  }, [propertyId, anchor, monthMeta.daysInMonth]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const byDate = useMemo(() => {
    const m = new Map<string, OccupancyHorizonDay>();
    days.forEach((d) => m.set(d.date, d));
    return m;
  }, [days]);

  const shiftMonth = useCallback((delta: -1 | 1) => {
    setAnchor(toDayKey(new Date(monthMeta.year, monthMeta.month + delta, 1)));
  }, [monthMeta.year, monthMeta.month]);

  const goToday = useCallback(() => {
    const d = new Date();
    setAnchor(toDayKey(new Date(d.getFullYear(), d.getMonth(), 1)));
  }, []);

  const monthLabel = monthMeta.first.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  const isCurrentMonth = anchor === toDayKey(
    new Date(parseDayKey(todayKey()).getFullYear(), parseDayKey(todayKey()).getMonth(), 1),
  );

  if (!propertyId) {
    return (
      <div style={{ padding: '1.5rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
        Select a property in the filter panel to see the month's inventory position.
      </div>
    );
  }

  const miniBtn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
    padding: '0.2rem 0.55rem', borderRadius: '0.375rem',
    border: '1px solid var(--border-color)', background: 'var(--bg-primary)',
    color: 'var(--text-secondary)', fontSize: '0.72rem', fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  };

  return (
    <div style={{ padding: '0.5rem 0.75rem 1rem', overflowY: 'auto', height: '100%' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
        marginBottom: '0.6rem',
      }}>
        <span style={{ fontSize: '0.9rem', fontWeight: 800 }}>{monthLabel}</span>
        {highVacancyCount > 0 && (
          <span style={{
            fontSize: '0.65rem', fontWeight: 700, padding: '0.1rem 0.45rem',
            borderRadius: '999px', border: '1px solid var(--warning, #d97706)',
            color: 'var(--warning, #d97706)', whiteSpace: 'nowrap',
          }}>
            ⚡ {highVacancyCount} high-vacancy
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" style={miniBtn} onClick={() => shiftMonth(-1)} title="Previous month">
          <IconSystem.chevronLeft size={12} />
        </button>
        <button
          type="button"
          style={{ ...miniBtn, opacity: isCurrentMonth ? 0.45 : 1 }}
          onClick={goToday}
          disabled={isCurrentMonth}
          title="Back to this month"
        >
          This month
        </button>
        <button type="button" style={miniBtn} onClick={() => shiftMonth(1)} title="Next month">
          <IconSystem.chevronRight size={12} />
        </button>
        <button type="button" style={miniBtn} onClick={load} title="Refresh">
          <IconSystem.refresh size={12} />
        </button>
      </div>

      {error && <div className="form-error-message" style={{ marginBottom: '0.5rem' }}>{error}</div>}

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '0.3rem',
        opacity: loading ? 0.55 : 1, transition: 'opacity 0.15s ease',
      }}>
        {DOW.map((d) => (
          <div key={d} style={{
            fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.05em',
            textTransform: 'uppercase', color: 'var(--text-muted)',
            textAlign: 'center', paddingBottom: '0.15rem',
          }}>
            {d}
          </div>
        ))}

        {/* Leading blanks so the 1st lands on its real weekday. */}
        {Array.from({ length: monthMeta.leading }).map((_, i) => (
          <div key={`pad-${i}`} />
        ))}

        {Array.from({ length: monthMeta.daysInMonth }).map((_, i) => {
          const key = toDayKey(new Date(monthMeta.year, monthMeta.month, i + 1));
          const day = byDate.get(key);
          if (!day) {
            // Inventory not yet generated for this night — say so rather than
            // rendering a zero, which would read as "sold out".
            return (
              <div
                key={key}
                style={{
                  minHeight: '4.6rem', borderRadius: '0.5rem',
                  border: '1px dashed var(--border-color)',
                  padding: '0.35rem 0.4rem', background: 'var(--bg-secondary)',
                }}
              >
                <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                  {i + 1}
                </div>
                <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                  {loading ? '…' : 'no data'}
                </div>
              </div>
            );
          }
          return (
            <OccupancyDayCell
              key={key}
              day={day}
              selected={key === selectedDate}
              onSelect={onSelectDate}
              variant="calendar"
            />
          );
        })}
      </div>

      <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
        Free rooms of total · bar &amp; colour = free % · footer{' '}
        <b>↑ arriving · staying over · ↓ departing</b> (bookings, not rooms) ·
        click a day for its guest list
      </div>
    </div>
  );
};

export default OccupancyCalendar;
